import { Assert } from '@cotera/utilities';
import type { DatasetOperationV20260601 } from '@cotera/api';

/** As much of a schema column as naming one requires. */
export type DatasetColumnName = { readonly name: string };

/**
 * The columns a dataset has, counting the edits it has not compacted yet.
 *
 * A column added, renamed or dropped lands in the operation log immediately and
 * reaches the artifact's schema only at compaction, which is deliberately
 * minutes behind (see `DATASET_OPERATION_COMPACTION_DELAY_MS`). So anything that
 * takes the artifact schema at its word spends that window telling someone who
 * just added a column that it does not exist — and offering, as the only
 * columns writable, a set the dataset has already moved past.
 *
 * Not a prediction of what compaction will produce: the server resolves the
 * schema exactly this way — artifact plus pending log — before it validates a
 * write, so this is the set a write is actually judged against.
 *
 * Row operations are listed and ignored rather than skipped, so a new operation
 * variant has to say here whether it changes a schema.
 */
export const datasetColumnsWithPendingOperations = (
  columns: readonly DatasetColumnName[],
  operations: readonly DatasetOperationV20260601[]
): DatasetColumnName[] =>
  operations.reduce<DatasetColumnName[]>(
    (current, operation) => {
      switch (operation.type) {
        case 'add_column':
          // The artifact can already carry the column: an operation stays
          // listed until the transaction that folds it in claims it, and the
          // two are read separately here.
          return current.some((column) => column.name === operation.columnName)
            ? current
            : [...current, { name: operation.columnName }];
        case 'rename_column':
          return current.map((column) =>
            column.name === operation.oldColumnName
              ? { name: operation.newColumnName }
              : column
          );
        case 'delete_column':
          return current.filter(
            (column) => column.name !== operation.columnName
          );
        case 'update_cell':
        case 'insert_row':
        case 'delete_row':
          return current;
        default:
          return Assert.unreachable(operation);
      }
    },
    [...columns]
  );
